import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const CreateChallanModal = ({ isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    invoiceId: '',
    dispatchDate: new Date().toISOString().split('T')[0],
    expectedDelivery: '',
    transportMode: 'courier',
    trackingNumber: '',
    driverName: '',
    vehicleNumber: '',
    notes: ''
  });

  const availableInvoices = [
    { value: 'INV-2025-001', label: 'INV-2025-001 - AJIO (₹45,680)', client: 'AJIO', amount: '₹45,680' },
    { value: 'INV-2025-002', label: 'INV-2025-002 - Fashion Hub (₹78,920)', client: 'Fashion Hub Pvt Ltd', amount: '₹78,920' },
    { value: 'INV-2025-005', label: 'INV-2025-005 - Trendy Wear (₹34,560)', client: 'Trendy Wear Co.', amount: '₹34,560' }
  ];

  const transportModeOptions = [
    { value: 'courier', label: 'Courier Service' },
    { value: 'own-vehicle', label: 'Own Vehicle' },
    { value: 'third-party', label: 'Third Party Logistics' },
    { value: 'pickup', label: 'Client Pickup' }
  ];

  if (!isOpen) return null;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Auto-calculate expected delivery date
    if (field === 'dispatchDate' || field === 'transportMode') {
      const dispatchDate = new Date(field === 'dispatchDate' ? value : formData.dispatchDate);
      const deliveryDate = new Date(dispatchDate);
      
      // Add delivery days based on transport mode
      const deliveryDays = {
        'courier': 3,
        'own-vehicle': 1,
        'third-party': 2,
        'pickup': 0
      };
      
      const mode = field === 'transportMode' ? value : formData.transportMode;
      deliveryDate.setDate(deliveryDate.getDate() + deliveryDays[mode]);
      
      setFormData(prev => ({
        ...prev,
        expectedDelivery: deliveryDate.toISOString().split('T')[0]
      }));
    }

    // Generate tracking number for courier
    if (field === 'transportMode' && value === 'courier') {
      const trackingNumber = 'TRK' + Date.now().toString().slice(-9);
      setFormData(prev => ({
        ...prev,
        trackingNumber
      }));
    }
  };

  const handleSave = () => {
    if (!formData.invoiceId || !formData.dispatchDate) {
      alert('Please select an invoice and dispatch date');
      return;
    }

    const selectedInvoice = availableInvoices.find(inv => inv.value === formData.invoiceId);
    const challanData = {
      ...formData,
      client: selectedInvoice.client,
      amount: selectedInvoice.amount,
      status: 'pending'
    };

    onSave(challanData);
    onClose();
    
    // Reset form
    setFormData({
      invoiceId: '',
      dispatchDate: new Date().toISOString().split('T')[0],
      expectedDelivery: '',
      transportMode: 'courier',
      trackingNumber: '',
      driverName: '',
      vehicleNumber: '',
      notes: ''
    });
  };

  const selectedInvoice = availableInvoices.find(inv => inv.value === formData.invoiceId);

  return (
    <div className="fixed inset-0 bg-black/50 z-1001 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <Icon name="Truck" size={24} className="text-primary" />
            <div>
              <h2 className="text-xl font-semibold text-foreground">Create Delivery Challan</h2>
              <p className="text-sm text-muted-foreground">Generate challan for order dispatch</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            iconName="X"
            iconSize={20}
          />
        </div>

        {/* Modal Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          <div className="space-y-6">
            {/* Invoice Selection */}
            <div>
              <Select
                label="Select Invoice"
                placeholder="Choose invoice to dispatch"
                options={availableInvoices}
                value={formData.invoiceId}
                onChange={(value) => handleInputChange('invoiceId', value)}
                required
                searchable
              />
              
              {selectedInvoice && (
                <div className="mt-3 p-3 bg-muted/30 border border-border rounded-lg">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-medium text-foreground">{selectedInvoice.client}</div>
                      <div className="text-sm text-muted-foreground">Invoice Amount: {selectedInvoice.amount}</div>
                    </div>
                    <Icon name="FileText" size={20} className="text-primary" />
                  </div>
                </div>
              )}
            </div>

            {/* Dispatch Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Dispatch Date"
                type="date"
                value={formData.dispatchDate}
                onChange={(e) => handleInputChange('dispatchDate', e.target.value)}
                required
              />

              <Input
                label="Expected Delivery"
                type="date"
                value={formData.expectedDelivery}
                onChange={(e) => handleInputChange('expectedDelivery', e.target.value)}
                required
              />
            </div>

            {/* Transport Details */}
            <div>
              <Select
                label="Transport Mode"
                placeholder="Select transport mode"
                options={transportModeOptions}
                value={formData.transportMode}
                onChange={(value) => handleInputChange('transportMode', value)}
                required
              />
            </div>

            {/* Conditional Fields based on Transport Mode */}
            {formData.transportMode === 'courier' && (
              <Input
                label="Tracking Number"
                type="text"
                placeholder="Auto-generated tracking number"
                value={formData.trackingNumber}
                onChange={(e) => handleInputChange('trackingNumber', e.target.value)}
                description="Tracking number for courier service"
              />
            )}

            {(formData.transportMode === 'own-vehicle' || formData.transportMode === 'third-party') && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Driver Name"
                  type="text"
                  placeholder="Enter driver name"
                  value={formData.driverName}
                  onChange={(e) => handleInputChange('driverName', e.target.value)}
                  required
                />

                <Input
                  label="Vehicle Number"
                  type="text"
                  placeholder="Enter vehicle number"
                  value={formData.vehicleNumber}
                  onChange={(e) => handleInputChange('vehicleNumber', e.target.value)}
                  required
                />
              </div>
            )}

            {formData.transportMode === 'pickup' && (
              <div className="p-4 bg-warning/10 border border-warning/20 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Icon name="AlertTriangle" size={20} className="text-warning" />
                  <div>
                    <div className="font-medium text-warning">Client Pickup</div>
                    <div className="text-sm text-muted-foreground">
                      Client will collect the order from your location
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Additional Notes */}
            <Input
              label="Notes (Optional)"
              type="text"
              placeholder="Additional delivery instructions"
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
            />

            {/* Delivery Summary */}
            {selectedInvoice && (
              <div className="bg-muted/30 border border-border rounded-lg p-4">
                <h4 className="font-semibold text-foreground mb-3">Delivery Summary</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Client:</span>
                    <span className="font-medium">{selectedInvoice.client}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Invoice:</span>
                    <span className="font-medium">{formData.invoiceId}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Dispatch Date:</span>
                    <span className="font-medium">
                      {new Date(formData.dispatchDate).toLocaleDateString('en-IN')}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Expected Delivery:</span>
                    <span className="font-medium">
                      {formData.expectedDelivery ? 
                        new Date(formData.expectedDelivery).toLocaleDateString('en-IN') : 
                        '-'
                      }
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Transport:</span>
                    <span className="font-medium capitalize">
                      {formData.transportMode.replace('-', ' ')}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            variant="default" 
            onClick={handleSave}
            disabled={!formData.invoiceId || !formData.dispatchDate}
            iconName="Truck"
            iconPosition="left"
          >
            Create Challan
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateChallanModal;